import type {
  AccessPreferences,
  DemoAction,
  DemoState,
  FieldGuideItemId,
  FieldGuideSelections,
  ScreenId,
  ToolId,
  ToolSelections,
  ToolStatus,
} from '../types/demo'
import { storyActions, witnessOptions } from '../data/savanna'

const SCREEN_UNLOCKS: Partial<Record<ScreenId, FieldGuideItemId>> = {
  clues: 'clues',
  signalCheck: 'signalCheck',
  wateringMap: 'wateringMap',
  echo: 'echo',
}

export function getInitialAccess(): AccessPreferences {
  const prefersReduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  return { quietView: false, reducedMotion: prefersReduced, highContrast: false, largerText: false }
}

export function createInitialState(access: AccessPreferences): DemoState {
  return {
    accessPreferences: access,
    currentScreen: 'welcome',
    history: [],
    visitedScreens: ['welcome'],
    prequelStep: 0,
    prequelRoute: null,
    seed: null,
    arrivalReply: null,
    viewedClues: [],
    activeClue: null,
    sortingSelections: {},
    activeAction: null,
    actionEffects: {},
    witnessChoice: null,
    witnessEffects: {},
    fieldGuideSelections: {
      signalCheck: {},
      wateringMap: {},
      signalCheckStatus: 'open',
      wateringMapStatus: 'open',
    },
    unlockedItems: [],
    pauseReturnScreen: null,
    resumeScreen: null,
    leftVisit: false,
    recap: null,
  }
}

function withTool(
  selections: FieldGuideSelections,
  tool: ToolId,
  values: ToolSelections,
  status: ToolStatus,
): FieldGuideSelections {
  return tool === 'signalCheck'
    ? { ...selections, signalCheck: values, signalCheckStatus: status }
    : { ...selections, wateringMap: values, wateringMapStatus: status }
}

function addUnique<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list : [...list, item]
}

function goTo(state: DemoState, screen: ScreenId, recap?: string): DemoState {
  const unlock = SCREEN_UNLOCKS[screen]
  const leavingPause = state.currentScreen === 'pause'
  const history =
    screen === state.currentScreen || leavingPause ? state.history : [...state.history, state.currentScreen]
  return {
    ...state,
    currentScreen: screen,
    history,
    visitedScreens: addUnique(state.visitedScreens, screen),
    unlockedItems: unlock ? addUnique(state.unlockedItems, unlock) : state.unlockedItems,
    pauseReturnScreen: leavingPause ? null : state.pauseReturnScreen,
    leftVisit: false,
    recap: recap ?? null,
  }
}

export function demoReducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    case 'GO':
      return goTo(state, action.screen, action.recap)
    case 'BACK': {
      if (state.history.length === 0) return goTo(state, 'welcome')
      const history = state.history.slice(0, -1)
      const previous = state.history[state.history.length - 1]
      return { ...state, history, currentScreen: previous, recap: null, leftVisit: false }
    }
    case 'SET_ACCESS':
      return { ...state, accessPreferences: { ...state.accessPreferences, [action.key]: action.value } }
    case 'SET_PREQUEL_STEP':
      return { ...state, prequelStep: action.step }
    case 'COMPLETE_PREQUEL':
      return {
        ...goTo(state, 'bridge', action.recap),
        prequelRoute: action.route,
        unlockedItems: addUnique(state.unlockedItems, 'purrAnchor'),
      }
    case 'PLANT_SEED':
      return { ...state, seed: action.seed, unlockedItems: addUnique(state.unlockedItems, 'seed') }
    case 'SET_ARRIVAL_REPLY':
      return { ...state, arrivalReply: action.reply }
    case 'VIEW_CLUE':
      return { ...state, activeClue: action.clue, viewedClues: addUnique(state.viewedClues, action.clue) }
    case 'SORT':
      return { ...state, sortingSelections: { ...state.sortingSelections, [action.card]: action.value } }
    case 'CHOOSE_ACTION': {
      const chosen = storyActions.find((item) => item.id === action.action)
      return { ...state, activeAction: action.action, actionEffects: { ...(chosen?.effects ?? {}) } }
    }
    case 'REPLAY_ROUTE': {
      const cleared: DemoState = {
        ...state,
        activeAction: null,
        actionEffects: {},
        witnessChoice: null,
        witnessEffects: {},
      }
      return goTo(cleared, 'action')
    }
    case 'CHOOSE_WITNESS': {
      const chosen = witnessOptions.find((item) => item.id === action.witness)
      return { ...state, witnessChoice: action.witness, witnessEffects: { ...(chosen?.effects ?? {}) } }
    }
    case 'SET_TOOL_FIELD': {
      const selections = state.fieldGuideSelections[action.tool]
      const current = selections[action.field]
      const next = { ...selections }
      if (current === action.value) delete next[action.field]
      else next[action.field] = action.value
      return { ...state, fieldGuideSelections: withTool(state.fieldGuideSelections, action.tool, next, 'open') }
    }
    case 'SET_TOOL_STATUS': {
      const values = action.status === 'blank' ? {} : state.fieldGuideSelections[action.tool]
      return {
        ...state,
        fieldGuideSelections: withTool(state.fieldGuideSelections, action.tool, values, action.status),
      }
    }
    case 'OPEN_PAUSE':
      if (state.currentScreen === 'pause') return state
      return { ...state, pauseReturnScreen: state.currentScreen, currentScreen: 'pause', recap: null }
    case 'CLOSE_PAUSE':
      return {
        ...state,
        currentScreen: state.pauseReturnScreen ?? 'welcome',
        pauseReturnScreen: null,
      }
    case 'LEAVE': {
      const from = state.currentScreen === 'pause' ? state.pauseReturnScreen : state.currentScreen
      return {
        ...state,
        resumeScreen: from && from !== 'welcome' ? from : state.resumeScreen,
        currentScreen: 'welcome',
        pauseReturnScreen: null,
        leftVisit: true,
        recap: null,
      }
    }
    case 'RESUME':
      if (!state.resumeScreen) return state
      return { ...goTo(state, state.resumeScreen), resumeScreen: null }
    case 'RESTART':
      return createInitialState(state.accessPreferences)
    case 'CLEAR_RECAP':
      return { ...state, recap: null }
    default:
      return state
  }
}
